import type {
  DateHolidayWarning,
  TermYearLookup,
  VicDatesSnapshot,
  VicGovApiDateEntry,
  VicGovApiResponse,
  VicPublicHoliday,
  VicSchoolHolidayRange,
  VicSchoolTermRange,
} from './vicDatesTypes';
import type { SchoolTerm } from './types';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const isIsoDate = (value: string): boolean => ISO_DATE_PATTERN.test(value);

const compareIso = (a: string, b: string): number => a.localeCompare(b);

const isDateInRange = (iso: string, start: string, end: string): boolean =>
  compareIso(iso, start) >= 0 && compareIso(iso, end) <= 0;

const termFromName = (
  name: string,
): { year: number; term: SchoolTerm } | undefined => {
  const match = name.match(/^Term ([1-4]) (\d{4})/i);
  if (!match) {
    return undefined;
  }
  return {
    term: Number(match[1]) as SchoolTerm,
    year: Number(match[2]),
  };
};

const collectRangeDates = (
  entries: VicGovApiDateEntry[],
  startPattern: RegExp,
  endPattern: RegExp,
  groupKey: (entry: VicGovApiDateEntry) => string | undefined,
): Map<string, { starts: string[]; ends: string[]; label?: string }> => {
  const groups = new Map<
    string,
    { starts: string[]; ends: string[]; label?: string }
  >();

  for (const entry of entries) {
    if (!isIsoDate(entry.date)) {
      continue;
    }

    const key = groupKey(entry);
    if (!key) {
      continue;
    }

    const group = groups.get(key) ?? { starts: [], ends: [] };
    if (startPattern.test(entry.name)) {
      group.starts.push(entry.date);
    } else if (endPattern.test(entry.name)) {
      group.ends.push(entry.date);
    }
    if (!group.label) {
      group.label = entry.name.replace(/\s*-\s*(Start|End).*$/i, '').trim();
    }
    groups.set(key, group);
  }

  return groups;
};

export const buildVicDatesSnapshot = (
  schoolTerms: VicGovApiDateEntry[],
  schoolHolidays: VicGovApiDateEntry[],
  publicHolidays: VicGovApiDateEntry[],
  updatedAt = new Date().toISOString(),
): VicDatesSnapshot => {
  const termGroups = collectRangeDates(
    schoolTerms,
    /start date/i,
    /end date/i,
    (entry) => {
      const parsed = termFromName(entry.name);
      return parsed ? `${parsed.year}-${parsed.term}` : undefined;
    },
  );

  const terms: VicSchoolTermRange[] = [...termGroups.entries()]
    .map(([key, group]) => {
      const parsed = termFromName(group.label ?? key);
      if (!parsed || group.starts.length === 0 || group.ends.length === 0) {
        return null;
      }
      return {
        year: parsed.year,
        term: parsed.term,
        start: [...group.starts].sort(compareIso)[0],
        end: [...group.ends].sort(compareIso).at(-1)!,
      };
    })
    .filter((value): value is VicSchoolTermRange => value !== null)
    .sort(
      (a, b) =>
        compareIso(a.start, b.start) || a.year - b.year || a.term - b.term,
    );

  const holidayGroups = collectRangeDates(
    schoolHolidays,
    /holiday start date/i,
    /holiday end date/i,
    (entry) => entry.name.replace(/\s*-\s*Holiday (Start|End) date.*$/i, ''),
  );

  const normalizedSchoolHolidays: VicSchoolHolidayRange[] = [
    ...holidayGroups.values(),
  ]
    .filter((group) => group.starts.length > 0 && group.ends.length > 0)
    .map((group) => ({
      name: group.label,
      start: [...group.starts].sort(compareIso)[0],
      end: [...group.ends].sort(compareIso).at(-1)!,
    }))
    .sort((a, b) => compareIso(a.start, b.start));

  const normalizedPublicHolidays: VicPublicHoliday[] = publicHolidays
    .filter((entry) => isIsoDate(entry.date))
    .map((entry) => ({
      date: entry.date,
      name: entry.name.replace(/\s*-\s*As .*$/i, '').trim(),
    }))
    .sort((a, b) => compareIso(a.date, b.date));

  return {
    updatedAt,
    terms,
    schoolHolidays: normalizedSchoolHolidays,
    publicHolidays: normalizedPublicHolidays,
  };
};

export const normalizeVicGovResponses = (
  responses: {
    schoolTerms: VicGovApiResponse;
    schoolHolidays: VicGovApiResponse;
    publicHolidays: VicGovApiResponse;
  },
  updatedAt = new Date().toISOString(),
): VicDatesSnapshot =>
  buildVicDatesSnapshot(
    responses.schoolTerms.dates ?? [],
    responses.schoolHolidays.dates ?? [],
    responses.publicHolidays.dates ?? [],
    updatedAt,
  );

export const termFromDate = (
  iso: string,
  snapshot: VicDatesSnapshot,
): TermYearLookup | undefined => {
  if (!isIsoDate(iso)) {
    return undefined;
  }

  for (const range of snapshot.terms) {
    if (isDateInRange(iso, range.start, range.end)) {
      return { term: range.term, year: range.year };
    }
  }

  const nextTerm = snapshot.terms.find(
    (range) => compareIso(range.start, iso) > 0,
  );
  if (nextTerm) {
    return { term: nextTerm.term, year: nextTerm.year };
  }

  return undefined;
};

export const dateHolidayWarning = (
  iso: string,
  snapshot: VicDatesSnapshot,
): DateHolidayWarning | undefined => {
  if (!isIsoDate(iso)) {
    return undefined;
  }

  const publicHoliday = snapshot.publicHolidays.find(
    (holiday) => holiday.date === iso,
  );
  if (publicHoliday) {
    return { kind: 'publicHoliday', name: publicHoliday.name };
  }

  const schoolHoliday = snapshot.schoolHolidays.find((holiday) =>
    isDateInRange(iso, holiday.start, holiday.end),
  );
  if (schoolHoliday) {
    return { kind: 'schoolHoliday', name: schoolHoliday.name };
  }

  return undefined;
};

export const formatHolidayWarning = (
  iso: string,
  warning: DateHolidayWarning,
  formatDate: (iso: string) => string = (value) => value,
): string => {
  const label = formatDate(iso);
  if (warning.kind === 'publicHoliday') {
    return `${label} is a public holiday (${warning.name})`;
  }
  if (warning.name) {
    return `${label} falls in school holidays (${warning.name})`;
  }
  return `${label} falls in school holidays`;
};
